import { Box, InputAdornment, TextField, TextFieldProps } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSnackbar } from "notistack";
import { CopyToClipboard } from "react-copy-to-clipboard";

type Props = Omit<TextFieldProps, "onChange" | "value"> & {
  value?: string | null;
  copyValue?: string;
  emptyValue?: string;
};

export const CopyableTextField = ({
  value,
  copyValue,
  emptyValue = "",
  fullWidth = true,
  variant = "filled",
  InputProps,
  InputLabelProps,
  ...rest
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const displayValue = value || emptyValue;
  const textToCopy = copyValue ?? value ?? "";
  const isCopyEnabled = textToCopy.length > 0;

  return (
    <CopyToClipboard
      text={textToCopy}
      onCopy={
        isCopyEnabled
          ? () => enqueueSnackbar("Copied to clipboard", { variant: "success" })
          : undefined
      }
    >
      <Box
        sx={{
          width: fullWidth ? "100%" : "auto",
          cursor: isCopyEnabled ? "pointer" : "default",
          "& .MuiInputBase-root": {
            cursor: isCopyEnabled ? "pointer" : "default",
            backgroundColor: "rgba(0, 0, 0, 0.06)",
          },
          "& .MuiInputBase-root:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.09)",
          },
          "& .MuiInputBase-input": {
            cursor: isCopyEnabled ? "pointer" : "default",
            WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
          },
          "& .MuiInputLabel-root": {
            cursor: isCopyEnabled ? "pointer" : "default",
          },
        }}
      >
        <TextField
          {...rest}
          value={displayValue}
          fullWidth={fullWidth}
          variant={variant}
          InputLabelProps={{
            shrink: true,
            ...InputLabelProps,
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position='end'>
                <ContentCopyIcon color={isCopyEnabled ? "action" : "disabled"} />
              </InputAdornment>
            ),
            ...InputProps,
          }}
        />
      </Box>
    </CopyToClipboard>
  );
};
